import { useCallback, useEffect, useRef, useState } from 'react'
import { AlertCircle, FileJson, Info, ListChecks, Loader2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formsApi } from '@/api/formsApi'
import { fieldLibraryApi } from '@/api/fieldLibraryApi'
import { userGroupsApi } from '@/api/userGroupsApi'
import { optionSetsApi } from '@/api/optionSetsApi'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { buildFormCopyPayload } from '@/lib/formCopy'
import {
  parseFormExport,
  planFormImport,
  toImportedFormSource,
  type FormImportBlocker,
  type FormImportPlan,
} from '@/lib/formTransfer'
import {
  OPTION_SET_PERMISSION_MESSAGE,
  applyOptionSetChoices,
  effectiveChoice,
  needsChoice,
  optionSetOutcome,
  optionSetReferences,
  planOptionSetImport,
  type OptionSetChoice,
  type OptionSetImportPlan,
  type OptionSetImportStatus,
} from '@/lib/formImportOptionSets'
import type { FormDefinition } from '@/types/cgt'

interface ImportFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (def: FormDefinition) => void
  /**
   * Whether the form open behind this dialog has unsaved edits. Import does not
   * touch it, but a successful import OPENS the new form, and navigating away
   * would discard them — so say so rather than losing them silently. The page
   * already takes the same care before switching versions.
   */
  openFormHasUnsavedChanges?: boolean
}

/**
 * Create a form from a JSON file the builder exported (z8nrz7ep1z) — the
 * counterpart to the toolbar's Export.
 *
 * Always creates a NEW form. It never writes into the form currently open and
 * never touches a saved version, so there is no path here that can damage an
 * existing definition. The write is `POST /form-definitions/import`: the same
 * create every new-form save runs, plus the option sets the form's dropdowns
 * need, in one transaction (z8nrz7f3qt).
 *
 * Two-step on purpose, mirroring the procedure-template import: pick a file,
 * see what importing it into THIS tenant will actually produce, then commit.
 * A reference the tenant does not have is cleared and reported rather than
 * carried through — a signer or completer group id with no group behind it is
 * refused by the server, so passing one on would turn a fine import into a
 * failed save with an opaque message.
 */
export function ImportFormDialog({
  open,
  onOpenChange,
  onCreated,
  openFormHasUnsavedChanges = false,
}: ImportFormDialogProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [reading, setReading] = useState(false)
  const [blockers, setBlockers] = useState<FormImportBlocker[]>([])
  const [plan, setPlan] = useState<FormImportPlan | null>(null)
  const [name, setName] = useState('')
  const [optionSetPlan, setOptionSetPlan] = useState<OptionSetImportPlan | null>(null)
  const [choices, setChoices] = useState<Record<string, OptionSetChoice>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { can } = usePermission()
  const canCreateOptionSets = can(PERMISSIONS.optionSetsManage)

  const reset = useCallback(() => {
    setFileName(null)
    setReading(false)
    setBlockers([])
    setPlan(null)
    setOptionSetPlan(null)
    setChoices({})
    setName('')
    setError(null)
    if (fileRef.current) fileRef.current.value = ''
  }, [])

  // Re-seed on open so an abandoned attempt never leaks into the next one.
  useEffect(() => {
    if (open) reset()
  }, [open, reset])

  async function handleFile(file: File) {
    setReading(true)
    setBlockers([])
    setPlan(null)
    setOptionSetPlan(null)
    setChoices({})
    setError(null)
    setFileName(file.name)
    try {
      const parsed = parseFormExport(await file.text())
      if (!parsed.ok) {
        setBlockers(parsed.blockers)
        return
      }
      // What this tenant has, so the plan can say which of the file's soft
      // references resolve. `listAllFields` pages past the 100-row API cap —
      // the same call this page's Excel import already makes. The name query
      // is scoped to the source name so the de-duplicated default can be
      // computed without downloading the form catalog.
      //
      // Option sets: the ones the file carries are resolved by the server — the
      // same resolver the commit runs. The ones it does not (a version-1 file)
      // can only be kept if this tenant has that exact id.
      const setRefs = optionSetReferences(parsed.form, parsed.optionSets)
      const [libraryFields, groups, byName, catalog, resolutions, knownSets] = await Promise.all([
        fieldLibraryApi.listAllFields(),
        userGroupsApi.list(),
        formsApi.listDefinitions({ q: parsed.form.name, take: 100 }),
        formsApi.listDefinitions({ take: 100 }),
        setRefs.carried.length > 0
          ? formsApi.resolveImportOptionSets(
              setRefs.carried.map(set => ({
                ref: set.ref,
                name: set.name,
                // Ids are irrelevant to the match (labels are compared), and
                // the commit mints real ones.
                options: set.values.map((label, i) => ({ id: String(i), label })),
              })),
            )
          : Promise.resolve([]),
        setRefs.uncarried.length > 0
          ? optionSetsApi.list({ ids: setRefs.uncarried.map(r => r.ref).join(',') })
          : Promise.resolve({ items: [], total: 0 }),
      ])
      setOptionSetPlan(
        planOptionSetImport(setRefs, resolutions, new Set(knownSets.items.map(s => s.id))),
      )
      // Only claim a referenced form is absent when the whole catalog was
      // seen. A truncated page would otherwise report forms as missing that
      // are merely on page two, sending the author to look for something that
      // is already there.
      const sawWholeCatalog = catalog.items.length >= catalog.total
      const next = planFormImport(parsed.form, {
        libraryFieldIds: new Set(libraryFields.map(f => f.id)),
        // First field per curated name wins; the link is provenance, and every
        // reader resolves a system name to the whole set of fields carrying it.
        libraryFieldIdBySystemName: libraryFields.reduce((byName, f) => {
          const name = f.systemName?.trim()
          if (name && !byName.has(name)) byName.set(name, f.id)
          return byName
        }, new Map<string, string>()),
        userGroupIds: new Set(groups.map(g => g.id)),
        existingFormNames: byName.items.map(d => d.name),
        existingFormHandles: sawWholeCatalog
          ? new Set(catalog.items.map(d => d.handle).filter((h): h is string => !!h))
          : null,
      })
      setPlan(next)
      setName(next.name)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file.')
    } finally {
      setReading(false)
    }
  }

  async function handleImport() {
    if (!plan) return
    const trimmed = name.trim()
    if (!trimmed) return
    setBusy(true)
    setError(null)
    try {
      // Through the Copy Form builder, which re-mints every section, field and
      // table row/column id and rewrites every `{token}` to match. Re-minting
      // is a correctness requirement, not hygiene: ProcessStepConnection is
      // unique on (tenantId, fieldId), so a preserved field id would make this
      // form silently share the source form's process configuration.
      //
      // Option-set choices are applied first: an inline choice rewrites the
      // fields, and the copy builder carries `optionSetId` through unchanged,
      // so the refs the server binds survive the re-mint.
      const applied = applyOptionSetChoices(
        plan.form,
        optionSetPlan?.rows ?? [],
        choices,
        canCreateOptionSets,
      )
      const payload = buildFormCopyPayload(toImportedFormSource(applied.form), trimmed)
      const created = await formsApi.importDefinition(
        {
          ...payload,
          // Carried separately because the Copy flow does not emit it, and
          // widening that would change what copying a form does. Every form
          // save already sends this property, so the DTO accepts it.
          defaultRepeatCount: plan.form.defaultRepeatCount ?? null,
        },
        applied.optionSets,
      )
      onCreated(created)
      onOpenChange(false)
    } catch (e) {
      setError(importErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const optionSetBlockers = optionSetPlan?.blockers ?? []
  const canImport = !!plan && !!name.trim() && optionSetBlockers.length === 0

  return (
    <Dialog open={open} onOpenChange={o => !busy && onOpenChange(o)}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Import Form</DialogTitle>
          <DialogDescription>
            Creates a brand-new form from a JSON file exported by the form builder. The form you
            have open is not changed.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-3 py-2'>
          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => fileRef.current?.click()}
              disabled={busy || reading}
            >
              {reading ? (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              ) : (
                <FileJson className='mr-2 h-4 w-4' />
              )}
              Choose file
            </Button>
            <span className='truncate text-sm text-muted-foreground' title={fileName ?? undefined}>
              {fileName ?? 'No file chosen'}
            </span>
            <input
              ref={fileRef}
              type='file'
              accept='application/json,.json'
              className='hidden'
              onChange={e => {
                const file = e.target.files?.[0]
                if (file) void handleFile(file)
                // Cleared so re-picking the SAME file still fires a change.
                e.target.value = ''
              }}
            />
          </div>

          {(blockers.length > 0 || optionSetBlockers.length > 0) && (
            <div className='rounded-md border border-destructive/40 bg-destructive/5 p-3'>
              <p className='mb-1.5 flex items-center gap-1.5 text-sm font-medium text-destructive'>
                <AlertCircle className='h-4 w-4 shrink-0' />
                This file cannot be imported
              </p>
              <ul className='ml-6 list-disc space-y-0.5 text-sm text-destructive'>
                {blockers.map((b, i) => (
                  <li key={`${b.kind}-${i}`}>{b.message}</li>
                ))}
                {optionSetBlockers.map(message => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </div>
          )}

          {plan && (
            <>
              <div className='rounded-md border bg-muted/40 p-3 text-sm'>
                <p className='flex items-center gap-1.5 font-medium'>
                  <FileJson className='h-4 w-4 shrink-0 text-muted-foreground' />
                  {plan.sectionCount} section{plan.sectionCount === 1 ? '' : 's'},{' '}
                  {plan.fieldCount} field{plan.fieldCount === 1 ? '' : 's'}
                </p>
              </div>

              <div className='space-y-1.5'>
                <Label htmlFor='import-form-name'>Name</Label>
                <Input
                  id='import-form-name'
                  value={name}
                  onChange={e => setName(e.target.value)}
                  autoFocus
                />
                {!name.trim() && (
                  <p className='text-xs text-muted-foreground'>Give the imported form a name.</p>
                )}
              </div>

              {optionSetPlan && optionSetPlan.rows.length > 0 && (
                <OptionSetGroup
                  plan={optionSetPlan}
                  choices={choices}
                  canCreate={canCreateOptionSets}
                  onChoose={(ref, choice) => setChoices(prev => ({ ...prev, [ref]: choice }))}
                />
              )}

              {plan.notes.length > 0 && (
                <div className='rounded-md border border-amber-200 bg-amber-50 p-3'>
                  <p className='mb-1.5 flex items-center gap-1.5 text-sm font-medium text-amber-900'>
                    <Info className='h-4 w-4 shrink-0' />
                    Before you import
                  </p>
                  <ul className='ml-6 list-disc space-y-1 text-sm text-amber-900'>
                    {plan.notes.map((n, i) => (
                      <li key={`${n.kind}-${i}`}>{n.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {plan && openFormHasUnsavedChanges && (
            <p className='flex items-start gap-1.5 text-sm text-muted-foreground'>
              <Info className='mt-0.5 h-4 w-4 shrink-0' />
              The form you have open has unsaved changes. Importing opens the new form, so save
              them first if you want to keep them.
            </p>
          )}

          {error && (
            <p className='flex items-start gap-1.5 text-sm text-destructive'>
              <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={busy || !canImport}>
            {busy ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <Upload className='mr-2 h-4 w-4' />
            )}
            Import Form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * A server message the user cannot act on, replaced by one they can (AC3.5).
 * The preview binds every set the file names, so this is reachable only when a
 * set is deleted between preview and commit.
 */
function importErrorMessage(e: unknown): string {
  const message = e instanceof Error ? e.message : ''
  if (message.includes('Unknown optionSetId reference')) {
    return 'An option set this form uses no longer exists here. Choose the file again to refresh the preview.'
  }
  return message || 'Import failed.'
}

const STATUS_LABEL: Record<OptionSetImportStatus, string> = {
  reused: 'Reused',
  missing: 'Missing',
  conflict: 'Name conflict',
}

/**
 * The preview's Option sets group (z8nrz7f3qt AC2.1–2.4): each set the file
 * references, the fields that use it, its status here, and — for a missing or
 * conflicting set — the choice between creating it and importing its values
 * inline.
 */
function OptionSetGroup({
  plan,
  choices,
  canCreate,
  onChoose,
}: {
  plan: OptionSetImportPlan
  choices: Record<string, OptionSetChoice>
  canCreate: boolean
  onChoose: (ref: string, choice: OptionSetChoice) => void
}) {
  const anyChoice = plan.rows.some(needsChoice)
  return (
    <div className='space-y-2 rounded-md border p-3'>
      <p className='flex items-center gap-1.5 text-sm font-medium'>
        <ListChecks className='h-4 w-4 shrink-0 text-muted-foreground' />
        Option sets
      </p>
      {anyChoice && !canCreate && (
        <p className='text-sm text-muted-foreground'>{OPTION_SET_PERMISSION_MESSAGE}</p>
      )}
      <ul className='space-y-2'>
        {plan.rows.map(row => {
          const choice = effectiveChoice(row, choices, canCreate)
          return (
            <li key={row.ref} className='space-y-1 rounded border bg-muted/30 p-2 text-sm'>
              <div className='flex items-center justify-between gap-2'>
                <span className='truncate font-medium' title={row.sourceName}>
                  {row.sourceName}
                </span>
                <span
                  className={
                    row.resolution.status === 'reused'
                      ? 'shrink-0 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800'
                      : 'shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900'
                  }
                >
                  {STATUS_LABEL[row.resolution.status]}
                </span>
              </div>
              <p className='text-xs text-muted-foreground'>Used by: {row.fieldLabels.join(', ')}</p>
              {needsChoice(row) && canCreate && (
                <div className='flex gap-1.5' role='radiogroup' aria-label={`${row.sourceName}: how to import`}>
                  {(['inline', 'create'] as const).map(option => (
                    <Button
                      key={option}
                      type='button'
                      size='sm'
                      variant={choice === option ? 'default' : 'outline'}
                      role='radio'
                      aria-checked={choice === option}
                      className='h-7 text-xs'
                      onClick={() => onChoose(row.ref, option)}
                    >
                      {option === 'inline' ? 'Use inline options' : 'Create option set'}
                    </Button>
                  ))}
                </div>
              )}
              <p className='text-xs'>{optionSetOutcome(row, choice)}</p>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
